import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Navigate } from "react-router-dom";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { UserContext } from "context/UserContext";
import axiosInstance from "services/axiosConfig";
import BaseEnum from "enums/BaseEnum";
import { toast } from "react-toastify";
import PropTypes from "prop-types";

const allowedRoles = ["ROLE_MANAGER", "ROLE_SALES_RESPONSIBLE"];

function InfoItem({ label, value }) {
  return (
    <Grid item xs={12} md={6}>
      <MDTypography variant="button" color="text">
        <strong>{label}:</strong> {value || "-"}
      </MDTypography>
    </Grid>
  );
}
InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

function Customers() {
  const { user, loading: userLoading } = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedName, setSelectedName] = useState("");

  const hasAccess = useMemo(() => {
    if (!user || !user.roles) return false;
    return user.roles.some((role) => allowedRoles.includes(role));
  }, [user]);

  const fetchCustomers = async () => {
    if (!user || !hasAccess) return;
    setLoading(true);
    try {
      const endpoint = user.roles.includes("ROLE_MANAGER")
        ? "manager/getAllCustomers"
        : "sales/getAllCustomers";
      const response = await axiosInstance.get(endpoint);
      const customers = response.data || [];
      const formattedRows = customers.map((customer) => ({
        id: customer.id,
        name: customer.name || "N/A",
        tradeName: customer.ticariUnvan || "N/A",
        sector: customer.sector || "-",
        phone: customer.telefon || "-",
        email: customer.eposta || "-",
        responsible: customer.responsibleUserName || "-",
      }));
      setRows(formattedRows);
    } catch (error) {
      console.error("Customers: fetch error", error);
      toast.error("Müşteri listesi alınırken hata oluştu", { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userLoading && hasAccess) {
      fetchCustomers();
    }
  }, [userLoading, hasAccess]);

  const handleRowClick = async (params) => {
    setSelectedName(params.row.name);
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedCustomer(null);
    try {
      const response = await axiosInstance.get(
        `sales/get-customer/${encodeURIComponent(params.row.name)}`
      );
      setSelectedCustomer(response.data);
    } catch (error) {
      console.error("Customers detail fetch error", error);
      toast.error("Müşteri detayları alınırken hata oluştu", { position: "top-right" });
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = [
    { field: "name", headerName: BaseEnum.NAME, flex: 1, minWidth: 150 },
    { field: "tradeName", headerName: BaseEnum.TRADE_NAME, flex: 1, minWidth: 150 },
    { field: "sector", headerName: BaseEnum.SECTOR, flex: 1, minWidth: 140 },
    { field: "phone", headerName: BaseEnum.PHONE, flex: 1, minWidth: 140 },
    { field: "email", headerName: BaseEnum.EMAIL, flex: 1.2, minWidth: 180 },
    {
      field: "responsible",
      headerName: BaseEnum.RESPONSIBLE_USER,
      flex: 1,
      minWidth: 160,
    },
  ];

  if (!userLoading && (!user || !hasAccess)) {
    return <Navigate to="/orders/create" replace />;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={3}>
                <MDTypography variant="h5" fontWeight="medium">
                  Müşteri Listesi
                </MDTypography>
                <MDTypography variant="button" color="text">
                  {user?.roles?.includes("ROLE_MANAGER")
                    ? "Yöneticiler tüm müşterileri görebilir."
                    : "Satış sorumluları sadece kendi erişimindeki müşterileri görür."}
                </MDTypography>
              </MDBox>
              <MDBox p={3} pt={0}>
                <div style={{ width: "100%" }}>
                  <DataGrid
                    autoHeight
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    onRowClick={handleRowClick}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 },
                      },
                    }}
                    disableRowSelectionOnClick
                    localeText={{
                      toolbarColumns: "Sütunlar",
                      toolbarFilters: "Filtreler",
                      toolbarExport: "Dışa Aktar",
                      toolbarDensity: "Yoğunluk",
                      toolbarQuickFilterPlaceholder: "Ara...",
                      noRowsLabel: "Gösterilecek müşteri bulunamadı",
                    }}
                  />
                </div>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>{selectedCustomer?.name || selectedName}</DialogTitle>
          <DialogContent dividers>
            {detailLoading && (
              <MDTypography variant="body2" color="text">
                Yükleniyor...
              </MDTypography>
            )}
            {!detailLoading && selectedCustomer && (
              <>
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  Genel Bilgiler
                </MDTypography>
                <Grid container spacing={2}>
                  <InfoItem label={BaseEnum.TRADE_NAME} value={selectedCustomer.ticariUnvan} />
                  <InfoItem label={BaseEnum.EMAIL} value={selectedCustomer.eposta} />
                  <InfoItem label={BaseEnum.PHONE} value={selectedCustomer.telefon} />
                  <InfoItem label={BaseEnum.SECTOR} value={selectedCustomer.sector} />
                  <InfoItem label={BaseEnum.TAX_OFFICE} value={selectedCustomer.vergiDairesi} />
                  <InfoItem label={BaseEnum.TAX_NUMBER} value={selectedCustomer.vergiNumarasi} />
                  <InfoItem label={BaseEnum.ADDRESS} value={selectedCustomer.adres} />
                  <InfoItem
                    label={BaseEnum.RESPONSIBLE_USER}
                    value={selectedCustomer.responsibleUserName || "-"}
                  />
                  <InfoItem
                    label="Özel Müşteri"
                    value={selectedCustomer.isPrivate ? "Evet" : "Hayır"}
                  />
                </Grid>
                <Divider sx={{ my: 2 }} />
                <MDTypography variant="h6" fontWeight="medium" gutterBottom>
                  Fabrikalar
                </MDTypography>
                {selectedCustomer.fabrikalar && selectedCustomer.fabrikalar.length > 0 ? (
                  selectedCustomer.fabrikalar.map((factory, index) => (
                    <MDBox key={factory.fabrikaAdi + index} mb={2}>
                      <MDTypography variant="button" fontWeight="bold">
                        {factory.fabrikaAdi}
                      </MDTypography>
                      <Grid container spacing={1}>
                        <InfoItem
                          label={BaseEnum.FACTORY_MANAGER}
                          value={factory.fabrikaSorumlusu}
                        />
                        <InfoItem label={BaseEnum.FACTORY_PHONE} value={factory.fabrikaTelefon} />
                        <InfoItem label={BaseEnum.FACTORY_ADDRESS} value={factory.adres} />
                      </Grid>
                    </MDBox>
                  ))
                ) : (
                  <MDTypography variant="body2" color="text">
                    Fabrika bilgisi bulunmuyor.
                  </MDTypography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Kapat</Button>
          </DialogActions>
        </Dialog>
      </MDBox>
    </DashboardLayout>
  );
}

export default Customers;
